import { useCallback, useEffect, useRef } from 'react';
import { z } from 'zod';

import { CadRendererSnapshotEvent, CadRendererSyncNamespace } from '../broadcast-manager.js';
import { useSyncSnapshotHydration, useSyncSnapshotResponder } from '../sync-core.js';
import { arrayKeepingValid, pickLatestByKey } from '../sync-protocol.js';

import type { GraphicRenderStateOpts } from '@cadstrom/renderer';
import type { Utils } from '@cadstrom/sigma-engine-shared';
import type { CadRendererSyncCore } from '../sync-core.js';

export type SerializableGraphicRenderOpts = {
	tint: number;
	alpha: number;
};

const SerializableGraphicRenderOptsSchema = z.object({
	tint: z.number(),
	alpha: z.number(),
}) satisfies Utils.Zot<SerializableGraphicRenderOpts>;

export type SerializableGraphicRenderState = {
	selected: SerializableGraphicRenderOpts;
	hover: SerializableGraphicRenderOpts;
	unselected: SerializableGraphicRenderOpts;
	selectionNeutral: SerializableGraphicRenderOpts;
};

const SerializableGraphicRenderStateSchema = z.object({
	selected: SerializableGraphicRenderOptsSchema,
	hover: SerializableGraphicRenderOptsSchema,
	unselected: SerializableGraphicRenderOptsSchema,
	selectionNeutral: SerializableGraphicRenderOptsSchema,
}) satisfies Utils.Zot<SerializableGraphicRenderState>;

export type CadRendererLayerState = {
	layerSet: string;
	visibleLayerIds: readonly number[];
	colorOverrides: Readonly<Record<number, SerializableGraphicRenderState>>;
	updatedAt: number;
	sourceWindowId: string;
};

const CadRendererLayerStateSchema = z.object({
	layerSet: z.string(),
	visibleLayerIds: z.array(z.number()),
	colorOverrides: z.record(z.string(), SerializableGraphicRenderStateSchema),
	updatedAt: z.number(),
	sourceWindowId: z.string(),
}) satisfies Utils.Zot<CadRendererLayerState>;

export type LayerSnapshotPayload = {
	requestId?: string;
	layerStates: readonly CadRendererLayerState[];
};

const LayerSnapshotPayloadSchema = z.object({
	requestId: z.string().optional(),
	layerStates: arrayKeepingValid(CadRendererLayerStateSchema),
}) satisfies Utils.Zot<LayerSnapshotPayload>;

/**
 * Bridges layer visibility and color override state to the sync channel.
 *
 * It responds to peer snapshot requests with the current layer state and applies the newest layer
 * snapshot per layer set when receiving live updates or initial hydration responses.
 */
export function useLayerBridge({
	core,
	layerStates,
	updateLayerState,
}: {
	core: CadRendererSyncCore;
	layerStates: Readonly<Record<string, CadRendererLayerState>>;
	updateLayerState: (state: CadRendererLayerState, publish: boolean) => void;
}) {
	const layerStatesRef = useRef(layerStates);
	useEffect(() => {
		layerStatesRef.current = layerStates;
	}, [layerStates]);

	const createSnapshot = useCallback(
		(): LayerSnapshotPayload => ({
			layerStates: Object.values(layerStatesRef.current),
		}),
		[],
	);

	useSyncSnapshotResponder({
		core,
		namespace: CadRendererSyncNamespace.Layers,
		createPayload: createSnapshot,
	});

	useEffect(() => {
		return core.subscribe(CadRendererSyncNamespace.Layers, (envelope) => {
			if (envelope.event !== CadRendererSnapshotEvent.Snapshot) {
				return;
			}

			const payload = parseLayerSnapshot(envelope.payload);
			if (!payload) {
				return;
			}
			for (const state of payload.layerStates) {
				updateLayerState(state, false);
			}
		});
	}, [core, updateLayerState]);

	const applyHydrationResponses = useCallback(
		(responses: readonly LayerSnapshotPayload[]) => {
			const states = responses.flatMap((payload) => payload.layerStates);
			for (const state of pickLatestByKey(states, (state) => state.layerSet)) {
				updateLayerState(state, false);
			}
		},
		[updateLayerState],
	);

	useSyncSnapshotHydration({
		core,
		namespace: CadRendererSyncNamespace.Layers,
		parsePayload: parseLayerSnapshot,
		applyResponses: applyHydrationResponses,
	});
}

export function serializeGraphicRenderState(opts: GraphicRenderStateOpts): SerializableGraphicRenderState {
	return {
		selected: serializeGraphicRenderOpts(opts.selected),
		hover: serializeGraphicRenderOpts(opts.hover),
		unselected: serializeGraphicRenderOpts(opts.unselected),
		selectionNeutral: serializeGraphicRenderOpts(opts.selectionNeutral),
	};
}

export function deserializeGraphicRenderState(opts: SerializableGraphicRenderState): GraphicRenderStateOpts {
	return {
		selected: { ...opts.selected },
		hover: { ...opts.hover },
		unselected: { ...opts.unselected },
		selectionNeutral: { ...opts.selectionNeutral },
	};
}

function parseLayerSnapshot(value: unknown): LayerSnapshotPayload | null {
	const result = LayerSnapshotPayloadSchema.safeParse(value);
	return result.success ? result.data : null;
}

function serializeGraphicRenderOpts(opts: SerializableGraphicRenderOpts): SerializableGraphicRenderOpts {
	return {
		tint: opts.tint,
		alpha: opts.alpha,
	};
}
